// import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../AuthContext";
import { useNotification } from "../components/Notifications";
import { apiFetch } from "../api/client";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";

function decodeJwt(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return json;
    } catch (e) {
        return null;
    }
}

function Login() {
    const [form, setForm] = useState({
        email: "",
        password: "",
    });

    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const notify = useNotification();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // Navigation after signin is performed via the signin callback to avoid races

    const handleInputChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: null });
    };

    const loginUser = async (e) => {
        e.preventDefault();

        // client-side validation
        const nextErrors = {};
        if (!form.email) nextErrors.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Invalid email format.";
        if (!form.password) nextErrors.password = "Password is required.";
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            notify("error", { title: "Validation", text: "Please fix validation errors." });
            return;
        }

        if (loading) return;
        setLoading(true);

        try {
            const res = await apiFetch(`/api/account/sign-in`, {
                // Authenticate should not be tenant-scoped
                skipTenant: true,
                method: "POST",
                body: JSON.stringify({ email: form.email, password: form.password }),
            });

            if (!res.ok) {
                let parsed = null;
                try {
                    parsed = await res.json();
                } catch (err) {
                    notify("error", { title: "Login failed", text: res.statusText });
                    return;
                }
                const note = mapApiErrorToNotification(parsed, res.status);
                notify("error", note);
                return;
            }

            const data = await res.json();
            if (data?.succeeded) {
                const token = data?.data?.token || data?.data?.Token || null;
                let userId = null;
                let isSuper = false;
                if (token) {
                    const payload = decodeJwt(token);
                    userId = payload?.sub || payload?.nameid || payload?.uid || payload?.id || null;
                    // Prefer explicit flag in login response
                    const respIsSuper = data?.data?.isSuperAdmin ?? data?.data?.IsSuperAdmin ?? data?.isSuperAdmin ?? data?.IsSuperAdmin;
                    if (respIsSuper !== undefined) {
                        isSuper = Boolean(respIsSuper);
                    } else {
                        // Prefer roles returned in the API response (loginResponse.Roles) if present
                        const respRoles = data?.data?.roles || data?.data?.Roles || data?.roles || data?.Roles;
                        if (respRoles) {
                            if (Array.isArray(respRoles)) {
                                isSuper = respRoles.includes("SuperAdmin");
                            } else if (typeof respRoles === 'string') {
                                isSuper = respRoles === "SuperAdmin" || respRoles.split(',').map(r => r.trim()).includes("SuperAdmin");
                            }
                        } else {
                            // fallback to JWT payload
                            const roles = payload?.role || payload?.roles || (payload ? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] : undefined);
                            if (roles) {
                                if (Array.isArray(roles)) {
                                    isSuper = roles.includes("SuperAdmin");
                                } else if (typeof roles === 'string') {
                                    isSuper = roles === "SuperAdmin" || roles.split(',').map(r => r.trim()).includes("SuperAdmin");
                                }
                            }
                        }
                    }
                }

                // If user is superadmin, ensure any stored tenant slug is removed
                if (isSuper) {
                    try { localStorage.removeItem("tenant"); } catch (e) { }
                    // Backend's SignIn endpoint now handles clearing old refresh tokens
                }

                // Persist tenant slug if backend returned it and user is not SuperAdmin
                const tenantSlug = data?.data?.tenantSlug || data?.data?.TenantSlug || data?.tenantSlug || data?.TenantSlug || null;

                // Clear tenant for superadmin, set it for regular users
                if (isSuper) {
                    try { localStorage.removeItem("tenant"); } catch (e) { }
                } else if (tenantSlug) {
                    try { localStorage.setItem("tenant", tenantSlug); } catch (e) { }
                }

                // Set superadmin flag and clear it for non-superadmin users
                if (isSuper) {
                    try { localStorage.setItem("isSuperAdmin", "true"); } catch (e) { }
                } else {
                    try { localStorage.removeItem("isSuperAdmin"); } catch (e) { }
                }

                const userObj = { _id: userId || form.email, email: form.email, token };
                try { localStorage.setItem("user", JSON.stringify(userObj)); } catch (e) { }

                // Determine navigation path before signin to ensure correct routing
                let targetPath = "/";
                if (isSuper) {
                    targetPath = "/superadmin";
                } else if (tenantSlug) {
                    targetPath = `/${tenantSlug}/`;
                }

                // Debugging: log storage state and target path before signin
                console.debug("Login: isSuper=", isSuper, "tenant in storage=", localStorage.getItem("tenant"), "isSuperAdmin=", localStorage.getItem("isSuperAdmin"), "targetPath=", targetPath);

                // Call signin with full user object and provide a callback that
                // navigates after App has updated auth state to avoid races.
                // Unregister service worker and clear caches before navigation to avoid
                // serving stale cached app shell or API responses that can show previous
                // user's dashboard. The callback runs after AuthContext updates in App.
                authContext.signin(userObj, async () => {
                    try {
                        if ('serviceWorker' in navigator) {
                            const regs = await navigator.serviceWorker.getRegistrations();
                            for (const reg of regs) {
                                try { await reg.unregister(); } catch (e) { /* ignore */ }
                            }
                        }

                        if (window.caches && typeof window.caches.keys === 'function') {
                            const keys = await caches.keys();
                            for (const key of keys) {
                                try { await caches.delete(key); } catch (e) { /* ignore */ }
                            }
                        }
                    } catch (e) {
                        // ignore errors during cleanup
                    }

                    notify("success", { title: "Signed in", text: "Welcome to Stokraze." });
                    navigate(targetPath, { replace: true });
                });
            } else {
                const note = mapApiErrorToNotification(data, res.status);
                notify("error", note);
            }
        } catch (err) {
            console.error(err);
            notify("error", { title: "Network error", text: "Could not reach server." });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
    };

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 h-screen items-center place-items-center">
                <div className="flex justify-center items-center mx-auto p-4">
                    {/* Responsive artwork: single element, mobile constrained, desktop natural */}
                    <img
                        className="block sm:hidden max-w-full h-auto w-11/12 max-w-xs object-contain hide-on-very-small"
                        src={require("../assets/SignIn.png")}
                        alt=""
                    />

                    {/* Desktop / laptop: use original natural size */}
                    <img className="hidden sm:block" src={require("../assets/SignIn.png")} alt="" />
                </div>
                <div className="w-full max-w-md space-y-8 p-6 sm:p-10 rounded-lg">
                    <Link to="/" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Home
                    </Link>
                    <div>
                        <img
                            className="mx-auto h-20 w-auto"
                            src={require("../assets/stokraze.png")}
                            alt="Your Company"
                        />
                        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                            Signin to your account
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            Or{" "}
                            <span
                                className="font-medium text-purple-600 hover:text-purple-500"
                            >
                                start your 10-day free trial
                            </span>
                        </p>
                    </div>
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        {/* <input type="hidden" name="remember" defaultValue="true" /> */}
                        <div className="-space-y-px rounded-md shadow-sm">
                            <div>
                                <label htmlFor="email-address" className="sr-only">
                                    Email address
                                </label>
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    disabled={loading}
                                    className="relative block w-full rounded-t-md border-0 py-1.5 px-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="Email address"
                                    value={form.email}
                                    onChange={handleInputChange}
                                />
                                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                            </div>
                            <div>
                                <label htmlFor="password" className="sr-only">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    disabled={loading}
                                    className="relative block w-full rounded-b-md border-0 py-1.5 px-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="Password"
                                    value={form.password}
                                    onChange={handleInputChange}
                                />
                                {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                />
                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-sm text-gray-900"
                                >
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <Link to="/forgot-password" className="font-medium text-purple-600 hover:text-purple-500">
                                    Forgot your password?
                                </Link>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                onClick={loginUser}
                                disabled={loading}
                                aria-busy={loading}
                                className="group relative flex items-center justify-center w-full rounded-md bg-purple-600 py-3 px-4 text-base sm:text-sm font-semibold text-white hover:bg-[#7B2FF7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
                            >
                                {loading && (
                                    <svg className="animate-spin -ml-2 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                )}
                                {loading ? "Signing in..." : "Sign in"}
                            </button>
                            <p className="mt-2 text-center text-sm text-gray-600">
                                Or{" "}
                                <span
                                    className="font-medium text-purple-600 hover:text-purple-500"
                                >
                                    Don't Have an Account, Please{" "}
                                    <Link to="/register"> Register now </Link>
                                </span>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default Login;
